import { el } from "./dom.js";

/**
 * Portrait only (Umakhan, 29 Sep) — three layers, because no single one reaches every phone:
 *
 * 1. `manifest.webmanifest` says `"orientation": "portrait"` (installed Android honours it).
 * 2. Installed, `screen.orientation.lock("portrait")` where the platform allows it (Android in
 *    standalone / fullscreen); a refusal, synchronous or not, is not an error.
 * 3. iOS honours neither (research 11 §1.6), so a phone on its side (landscape, short, a coarse
 *    pointer — tablets and desktops untouched) gets a full-screen bank surface, "Turn your phone
 *    upright", over an inert app. Nothing is re-rendered and nothing re-runs: the screen, an open
 *    sheet or a ceremony in flight is exactly where it was when the phone turns back. An open
 *    sheet (a modal `<dialog>`, in the top layer) is hidden while on its side — app.css 33.
 */
export const UPRIGHT_QUERY = "(orientation: landscape) and (max-height: 500px) and (pointer: coarse)";

const NS = "http://www.w3.org/2000/svg";

interface MediaList {
  readonly matches: boolean;
  addEventListener(type: "change", fn: (e: { matches: boolean }) => void): void;
}

export interface UprightDeps {
  readonly doc: Document;
  readonly app: HTMLElement;
  readonly matchMedia: (q: string) => MediaList;
  /** Installed (display-mode standalone, or iOS's navigator.standalone). */
  readonly standalone: boolean;
  readonly orientation: { lock(o: string): Promise<unknown> } | undefined;
}

/** An upright phone, drawn node by node (no markup string). */
function phone(): SVGSVGElement {
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("focusable", "false");
  const body = document.createElementNS(NS, "rect");
  for (const [k, v] of Object.entries({ x: "7", y: "2.5", width: "10", height: "19", rx: "2.5" })) body.setAttribute(k, v);
  const home = document.createElementNS(NS, "path");
  home.setAttribute("d", "M11 18.5h2");
  svg.append(body, home);
  return svg;
}

export function installUpright(d: UprightDeps): HTMLElement {
  const overlay = el("div", { class: "upright", role: "alert", hidden: true }, [el("span", { class: "ic" }), el("b", {}, ["Turn your phone upright"])]);
  overlay.querySelector(".ic")?.append(phone());
  d.doc.body.append(overlay);

  const apply = (sideways: boolean): void => {
    overlay.hidden = !sideways;
    d.app.inert = sideways;
    d.doc.documentElement.dataset["upright"] = sideways ? "true" : "false";
  };
  const mq = d.matchMedia(UPRIGHT_QUERY);
  apply(mq.matches);
  mq.addEventListener("change", (e) => apply(e.matches));

  if (d.standalone && d.orientation !== undefined) {
    try {
      void d.orientation.lock("portrait").catch(() => undefined);
    } catch {
      // Not allowed here (iOS, a tab): layer 3 carries it.
    }
  }
  return overlay;
}
